import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { JiraLoginService } from '../shared/login-components/jira-login/jira-login.service';

interface TempoAccount {
  id: number;
  name: string;
  key: string;
}

interface TempoAccountsResponse {
  results: TempoAccount[];
}

interface TempoAccountLink {
  accountId: number;
  issueId?: number;
  issueKey?: string;
  projectId?: number;
}

interface TempoAccountLinksResponse {
  results: TempoAccountLink[];
}

@Injectable({ providedIn: 'root' })
export class TempoService {
  // Use Angular dev proxy in development to bypass CORS: /tempo/* → https://api.tempo.io/4/*
  private readonly tempoApiBase = '/tempo';

  constructor(private http: HttpClient, private jiraLoginService: JiraLoginService) {}

  private getAuthHeaders(tempoKey: string) {
    return new HttpHeaders({ Authorization: `Bearer ${tempoKey}` });
  }

  /** Fetch all Tempo accounts using stored Tempo bearer token */
  fetchAccounts(): Observable<TempoAccount[]> {
    return this.jiraLoginService.checkIfJiraCredentialsExistAndFetch().pipe(
      switchMap((cred) => {
        const headers = this.getAuthHeaders(cred.tempoKey);
        return this.http.get<TempoAccountsResponse>(`${this.tempoApiBase}/accounts?limit=1000`, { headers });
      }),
      map((res) => res?.results ?? []),
    );
  }

  /** Fetch account-links filtered by projectId to know which accounts are linked to a Jira project */
  fetchAccountLinksByProject(projectId: string | number): Observable<TempoAccountLink[]> {
    return this.jiraLoginService.checkIfJiraCredentialsExistAndFetch().pipe(
      switchMap((cred) => {
        const headers = this.getAuthHeaders(cred.tempoKey);
        // account-links supports filtering; limit high to cover most
        return this.http.get<TempoAccountLinksResponse>(`${this.tempoApiBase}/account-links?projectId=${projectId}&limit=1000`, { headers });
      }),
      map((res) => res?.results ?? []),
    );
  }

  /** Fetch account-links filtered by accountId to list related issue keys */
  fetchAccountLinksByAccount(accountId: number): Observable<TempoAccountLink[]> {
    return this.jiraLoginService.checkIfJiraCredentialsExistAndFetch().pipe(
      switchMap((cred) => {
        const headers = this.getAuthHeaders(cred.tempoKey);
        return this.http.get<TempoAccountLinksResponse>(`${this.tempoApiBase}/account-links?accountId=${accountId}&limit=1000`, { headers });
      }),
      map((res) => res?.results ?? []),
    );
  }
}
